/**
 * One collapsible region of the context panel.
 *
 * Plan: docs/plans/episode-agent-parity.md S9; layout in
 * docs/backend/episodes.md ("[Target] The context panel").
 *
 * The panel has three regions below the entities — todo, hypotheses, memory —
 * and they are the same shape: a header with a count, a chevron, and rows that
 * scroll inside without taking the header with them. This is that shape,
 * lifted out of `TodoBlock` where it already existed, so the three cannot
 * drift apart.
 *
 * A collapsed region keeps its header and its count, so the panel always says
 * how much it is hiding.
 */
import { useState, type JSX, type ReactNode } from "react";
import { Icon } from "../../components/ui";
import type { IconName } from "../../components/ui/Icon";

export interface PanelRegionProps {
  readonly title: string;
  readonly iconName: IconName;
  /** Shown beside the title — `1/3` for the todo, a bare count for the rest. */
  readonly count: string;
  /** Where the region sits when the user has not touched it. Todo follows its
   *  own rule (expanded while anything is unfinished); hypotheses and memory
   *  start collapsed. */
  readonly defaultCollapsed: boolean;
  /** Colour the count as done. */
  readonly complete?: boolean;
  readonly testId: string;
  readonly children: ReactNode;
}

export function PanelRegion({
  title,
  iconName,
  count,
  defaultCollapsed,
  complete,
  testId,
  children,
}: PanelRegionProps): JSX.Element {
  // null = follow the default; a click pins an explicit choice that survives
  // later updates.
  const [override, setOverride] = useState<boolean | null>(null);
  const collapsed = override ?? defaultCollapsed;

  return (
    <div className="mx-4 mb-2 flex min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--color-agent-plan-border)] bg-[var(--color-agent-plan-bg)]">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => { setOverride(!collapsed); }}
        data-testid={`${testId}-header`}
        className="flex w-full shrink-0 cursor-pointer items-center justify-between border-x-0 border-t-0 border-b border-[var(--color-agent-plan-header-border)] bg-transparent px-3.5 py-2 hover:bg-[var(--color-agent-plan-header-border)]/30"
      >
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[var(--color-agent-center-text-heading)]">
          <Icon name={iconName} size={15} />
          <span>{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-xs ${
              complete === true ? "text-green" : "text-[var(--color-agent-center-text-muted)]"
            }`}
          >
            {count}
          </span>
          {/* Chevron on the right — same affordance as the entity cards. */}
          <Icon
            name={collapsed ? "chevron-right" : "chevron-down"}
            size={14}
            className="text-[var(--color-agent-center-text-muted)]"
          />
        </div>
      </button>
      {!collapsed && (
        <div data-testid={`${testId}-rows`} className="min-h-0 overflow-y-auto px-3.5 py-1">
          {children}
        </div>
      )}
    </div>
  );
}
